if (!customElements.get('collection-model-list')) {
  customElements.define(
    'collection-model-list',
    class CollectionModelList extends HTMLElement {
      constructor() {
        super();

        this.contentContainer = this.querySelector('.collection-model-list-content');
        this.sectionWrapper = this.closest('.section');
        this.header = this.querySelector('.collection-model-list-header');
        this.description = this.querySelector('.collection-model-list-description');
        this.collectionTitle = this.dataset.collectionTitle;
        
        // Storefront API configuration - hardcoded in JS
        this.storefrontApiEndpoint = '/api/2024-01/graphql.json';
        this.storefrontAccessToken = window.theme.pad_finder_api_token;; // Replace with your actual token
        
        if (!this.contentContainer || !this.collectionTitle) {
          console.warn('CollectionModelList: Missing required data attributes');
          return;
        }
      }

      connectedCallback() {
        this.setupGA4ClickHandler();
        this.init();
      }

      async init() {
        try {
          const metaobjects = await this.fetchAllMetaobjects();

          if (metaobjects?.length > 0) {
            // Swap skeletons → real content (same bounding box, zero layout shift)
            this.removeSkeletonLoaders();
            const isRendered = this.renderMetaobjects(metaobjects);

            if (this.header && isRendered) {
              this.header.classList.add('visible');
            }
            if (this.description && isRendered) {
              this.description.classList.add('visible');
            }
          } else {
            this.removeSkeletonLoaders();
            this.sectionWrapper?.classList.add('hidden');
          }
        } catch (error) {
          console.error('CollectionModelList: Error fetching metaobjects', error);
          this.removeSkeletonLoaders();
          this.sectionWrapper?.classList.add('hidden');
        }
      }

      async fetchAllMetaobjects() {
        const allMetaobjects = [];
        let cursor = null;
        let hasNextPage = true;

        while (hasNextPage) {
          const response = await this.fetchMetaobjectsPage(cursor);
          
          if (response?.data?.metaobjects) {
            const edges = response.data.metaobjects.edges || [];
            
            for (const edge of edges) {
              if (edge.node) {
                allMetaobjects.push(edge.node);
              }
            }

            const pageInfo = response.data.metaobjects.pageInfo;
            hasNextPage = pageInfo?.hasNextPage ?? false;
            cursor = pageInfo?.endCursor ?? null;
          } else {
            hasNextPage = false;
          }
        }

        return allMetaobjects;
      }

      async fetchMetaobjectsPage(cursor = null) {
        // GraphQL query with brand filter
        const query = `
          query GetMetaobjects($first: Int!, $after: String) {
            metaobjects(
              first: $first, 
              after: $after, 
              type: "compatibility"
            ) {
              edges {
                node {
                  id
                  handle
                  type
                  fields {
                    key
                    value
                    reference {
                      ... on Collection {
                        id
                        title
                        handle
                      }
                      ... on MediaImage {
                        image {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        `;

        const variables = {
          first: 250,
          after: cursor,
          brandTitle: this.collectionTitle
        };

        try {
          const response = await fetch(this.storefrontApiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
            },
            body: JSON.stringify({
              query,
              variables
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          
          // Filter by brand matching collection title in the response
          if (data?.data?.metaobjects?.edges) {
            const filteredEdges = data.data.metaobjects.edges.filter(edge => {
              const brand = this.getBrandValue(edge.node);
              return brand && brand.toLowerCase() === this.collectionTitle.toLowerCase();
            });
            
            return {
              data: {
                metaobjects: {
                  edges: filteredEdges,
                  pageInfo: data.data.metaobjects.pageInfo
                }
              }
            };
          }

          return data;
        } catch (error) {
          console.error('CollectionModelList: Error fetching page', error);
          throw error;
        }
      }

      getBrandValue(metaobject) {
        const brandField = metaobject.fields?.find(field => field.key === 'brand');
        if (!brandField) return null;

        // If brand is a collection reference, get the title
        if (brandField.reference?.title) {
          return brandField.reference.title;
        }

        // Otherwise return the value directly
        return brandField.value;
      }

      getCollectionValue(metaobject) {
        const collectionField = metaobject.fields?.find(field => field.key === 'collection');
        if (!collectionField) return null;

        // If collection is a reference, get the title
        return collectionField.reference?.title ?? null;
      }
      getCollectionHandle(metaobject) {
        const collectionField = metaobject.fields?.find(field => field.key === 'collection');
        if (!collectionField) return null;

        // If collection is a reference, get the title
        return collectionField.reference?.handle ?? null;
      }

      getModelImage(metaobject) {
        const imageField = metaobject.fields?.find(field => field.key === 'model_image');
        if (!imageField) return null;
        if (imageField.reference?.image?.url) {
          const { url, altText, width, height } = imageField.reference.image;
          return { url, altText, width, height };
        }

        // File reference (no dimensions available — return URL only)
        if (imageField.reference?.url) {
          return { url: imageField.reference.url };
        }

        // File object
        if (imageField.file?.url) {
          return { url: imageField.file.url };
        }

        // Plain URL string fallback
        if (imageField.value && typeof imageField.value === 'string' && imageField.value.startsWith('http')) {
          return { url: imageField.value };
        }

        return null;
      }

      removeSkeletonLoaders() {
        if (!this.contentContainer) return;
        // Remove all skeleton loader items
        const skeletonItems = this.contentContainer.querySelectorAll('.collection-model-list-item--skeleton');
        skeletonItems.forEach(item => item.remove());
      }

      renderMetaobjects(metaobjects) {
        if (!this.contentContainer) return;

        const fragment = document.createDocumentFragment();
        let isExists = false;
        for (const metaobject of metaobjects) {
          const item = this.createMetaobjectItem(metaobject);
          if (item) {
            fragment.appendChild(item);
            isExists = true;
          }
        }

        // Append new content (skeletons already removed)
        this.contentContainer.appendChild(fragment);
        return isExists;
      }

      createMetaobjectItem(metaobject) {
        const collectionTitle = this.getCollectionValue(metaobject);
        const collectionHandle = this.getCollectionHandle(metaobject);
        const imageData = this.getModelImage(metaobject); // now returns { url, width, height, altText }

        // Create main item link
        const item = document.createElement('a');
        item.className = 'collection-model-list-item';
        item.setAttribute('href', `/collections/${collectionHandle}`);
        if (collectionTitle) item.setAttribute('data-ga4-detail', collectionTitle);

        // Create image wrapper div
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'image-wrapper';

        if (imageData?.url) {
          const img = document.createElement('img');
          img.className = 'modal-image';
          img.alt = imageData.altText || collectionTitle || 'Model image';
          img.loading = 'lazy';

          if (imageData.width && imageData.height) {
            img.width  = imageData.width;
            img.height = imageData.height;
            // Inline aspect-ratio as belt-and-suspenders for browsers that
            // use CSS aspect-ratio rather than attribute intrinsics.
            img.style.aspectRatio = `${imageData.width} / ${imageData.height}`;
          }
          // ──────────────────────────────────────────────────────────────────

          // Build responsive srcset — strip existing query params first
          const baseUrl = imageData.url.split('?')[0];
          const widths = [180, 320, 375, 414, 540, 600, 768, 900, 1024, 1080, 1200, 1366, 1440, 1600, 1728, 1944, 2160, 2376, 2592, 2732];

          // Only include srcset entries up to the image's natural width to avoid
          // up-scaling, which wastes bytes without improving quality.
          const naturalWidth = imageData.width || Infinity;
          const srcsetParts = widths
            .filter(w => w <= naturalWidth)
            .map(w => `${baseUrl}?width=${w} ${w}w`);

          img.srcset = srcsetParts.join(', ');
          img.sizes  = '(max-width: 767px) 85px, 105px';

          // src fallback — use the closest to the rendered display size
          img.src = `${baseUrl}?width=210`;

          imageWrapper.appendChild(img);
        } else {
          return null;
        }

        item.appendChild(imageWrapper);

        // Create title if available
        if (collectionTitle) {
          const title = document.createElement('h3');
          title.className = 'collection-model-list-item-title';
          title.textContent = collectionTitle;
          item.appendChild(title);
          return item;
        }

       return null;
      }

      setupGA4ClickHandler() {
        this.addEventListener('click', (e) => {
          const item = e.target.closest('.collection-model-list-item');
          if (!item) return;
          const config = window.GA4_EVENTS?.collectionCategories?.modelThumbnail;
          if (!config || typeof window.pushGA4 !== 'function' || typeof window.buildGA4Payload !== 'function') return;
          const modelName = item.getAttribute('data-ga4-detail') || item.querySelector('.collection-model-list-item-title')?.textContent?.trim() || '';
          const detail = (config.detail || '') + modelName;
          const payload = window.buildGA4Payload(config, { detail: detail });
          if (payload) window.pushGA4(payload, item);
        });
      }
    }
  );
}

